import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchSnowData } from './api';
import BenchmarkResults from './BenchmarkResults';
import BenchmarkDiagnostics from './BenchmarkDiagnostics';
import StormTiming from './StormTiming';
import StationQualification from './StationQualification';
import ResortMeasurementQuality from './ResortMeasurementQuality';

const date = value => new Date(value).toLocaleString('en-US', {
  timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
});

function inches(mm) {
  if (mm === null || !Number.isFinite(mm)) return 'Unavailable';
  if (mm === 0) return '0 in';
  if (mm / 25.4 < 0.1) return '<0.1 in';
  return `${(mm / 25.4).toFixed(1)} in`;
}

export default function ResortEvidence({ resort }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setError('');
    setData(null);
    fetchSnowData(`resorts/${encodeURIComponent(resort.id)}/evidence`, controller.signal)
      .then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(failure => { if (!controller.signal.aborted) setError(failure.message); });
    return () => controller.abort();
  }, [resort.id, attempt]);

  if (error) return <div role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Retry resort data</button></div>;
  if (!data) return <p role="status">Loading resort evidence…</p>;
  const area = data.mapAreas.find(item => item.skiAreaId === params.get('point')) || data.mapAreas[0];
  const snapshot = data.snapshots.find(item => item.id === params.get('snapshot')) || data.snapshots[0];
  const forecasts = (snapshot?.forecasts || []).filter(item => item.pointId === area?.skiAreaId);
  const update = (key, value) => setParams(previous => {
    const next = new URLSearchParams(previous);
    next.set(key, value);
    return next;
  });
  const historical = forecasts.length > 0 && forecasts.every(item => Date.parse(item.validUntil) < Date.parse(item.capturedAt));
  const expired = forecasts.length > 0 && forecasts.every(item => Date.parse(item.validUntil) < Date.now());

  return <>
    <dl className="rekkie-snow-readiness">
      <div><dt>Resort map identity</dt><dd>{data.mapAreas.length ? `${data.mapAreas.length} mapped area${data.mapAreas.length > 1 ? 's' : ''}` : 'Point mapping pending'}</dd></div>
      <div><dt>Forecast location</dt><dd>Map center · elevation unverified</dd></div>
      <div><dt>Snowfall observations</dt><dd>{data.resortMeasurements?.plots?.length ? `${data.resortMeasurements.plots.length} resort plots · review required` : data.stations.length ? `${data.stations.length} nearby candidates · review required` : 'Audit pending'}</dd></div>
      <div><dt>Model training</dt><dd>Awaiting qualified pairs</dd></div>
    </dl>
    <ResortMeasurementQuality report={data.resortMeasurements} />
    {data.snapshots.length > 0 && <div className="rekkie-snow-controls">
        {data.mapAreas.length > 1 && <label>Map area<select value={area.skiAreaId} onChange={event => update('point', event.target.value)}>
          {data.mapAreas.map(item => <option value={item.skiAreaId} key={item.skiAreaId}>{item.name}</option>)}
        </select></label>}
        <label>Model initialization (UTC)<select value={snapshot.id} onChange={event => update('snapshot', event.target.value)}>
          {data.snapshots.map(item => <option value={item.id} key={item.id}>{date(item.initializedAt)}</option>)}
        </select></label>
      </div>}
    {forecasts.length ? <section className="rekkie-snow-guidance" aria-label="NOAA snowfall guidance">
      <div className="rekkie-snow-detail-heading"><h3>NOAA snowfall guidance</h3>
        <span className="rekkie-snow-status">{historical ? 'Historical extraction' : expired ? 'Expired guidance' : 'Unverified guidance'}</span></div>
      <p>{area.name} · {area.latitude.toFixed(4)}, {area.longitude.toFixed(4)} · Uncalibrated grid values</p>
      <div className="rekkie-snow-source-grid">
        {forecasts.map(forecast => <article key={forecast.model}>
          <div className="rekkie-snow-detail-heading"><h3>{forecast.model}</h3><span className="rekkie-snow-status">{forecast.accumulationHours} h total</span></div>
          <div className="rekkie-snow-snowfall">{inches(forecast.snowfallMm)}</div>
          <p>{date(forecast.validFrom)} → {date(forecast.validUntil)} UTC</p>
          <p>Grid point {forecast.distanceKm.toFixed(2)} km from map center</p>
          <details><summary>Source details</summary>
            <p>Captured {date(forecast.capturedAt)} UTC</p>
            <p>Grid {forecast.gridLatitude.toFixed(4)}, {forecast.gridLongitude.toFixed(4)}</p>
            <p>Actual and model elevation: unverified</p>
            <p>Historical publication time: unverified</p>
            <a href={`${forecast.sourceUrl}.idx`} target="_blank" rel="noreferrer">NOAA field inventory ↗</a>
          </details>
        </article>)}
      </div>
      <p className="rekkie-snow-note">Different accumulation windows. These values do not establish base or summit snowfall.</p>
    </section> : <div className="rekkie-snow-empty"><h3>No extracted forecast data</h3><p>Source files need point extraction before snowfall can be shown here.</p></div>}

    {(snapshot?.failures || []).map(failure => <p key={failure.model} role="status">{failure.model}: {failure.error}</p>)}

    <BenchmarkResults report={data.benchmark} />
    <BenchmarkDiagnostics benchmark={data.benchmark} diagnostics={data.diagnostics} />
    <StormTiming benchmark={data.benchmark} report={data.timing} />

    {(data.comparisons || []).map(comparison => <section key={comparison.caseId} className="rekkie-snow-stations" aria-label="Historical station comparison">
      <div className="rekkie-snow-detail-heading"><h3>Historical station comparison</h3><span className="rekkie-snow-status">Station only · Provisional</span></div>
      <p>{comparison.station.name} · {comparison.station.elevationM} m</p>
      <p>{date(comparison.targetFrom)} → {date(comparison.targetUntil)} UTC</p>
      <div className="rekkie-snow-table-wrap"><table>
        <thead><tr><th>{comparison.model} forecast</th><th>Measured snowfall</th><th>Forecast − measured</th></tr></thead>
        <tbody><tr><td>{inches(comparison.forecastMm)}</td><td>{inches(comparison.observedMm)}</td>
          <td>{comparison.errorMm > 0 ? '+' : ''}{(comparison.errorMm / 25.4).toFixed(1)} in</td></tr></tbody>
      </table></div>
      <p>Matched measurement interval. Historical source availability assumes a {comparison.assumedLatencyHours}-hour delay; resort accuracy remains unverified.</p>
      <details><summary>Comparison provenance</summary>
        <p>Model initialization {date(comparison.initializedAt)} UTC</p>
        <p>Simulated issue {date(comparison.simulatedIssueAt)} UTC</p>
        <p>Generated {date(comparison.generatedAt)} UTC</p>
        <p>Grid {comparison.forecast.components[0].distanceKm.toFixed(2)} km from the observing station</p>
        <a href={comparison.observationSource.url} target="_blank" rel="noreferrer">NOAA observation source ↗</a>
      </details>
    </section>)}

    <section className="rekkie-snow-stations" aria-label="Snowfall observation candidates">
      <h3>Snowfall observation candidates</h3>
      {!data.stations.length && <p>No station audit saved.</p>}
      {data.stations.map(station => <details key={station.id} className="rekkie-snow-station">
        <summary>{station.name} · {station.distanceKm.toFixed(1)} km · {station.status === 'captured' ? station.recordCount ? 'Unreviewed' : 'No records in audit window' : 'Check failed'}</summary>
        <p>{station.id} · {station.elevationM === null ? 'Elevation unknown' : `${Math.round(station.elevationM)} m elevation`}</p>
        <StationQualification station={data.qualification?.stations.find(item => item.id === station.id)} />
        {station.status === 'captured' ? <>
          <p>Observed at the station. Resort exposure and measurement intervals remain unverified.</p>
          <div className="rekkie-snow-table-wrap"><table>
            <thead><tr><th>Winter</th><th>Numeric days</th><th>Trace</th><th>Missing / excluded</th><th>Time supplied</th></tr></thead>
            <tbody>{station.winters.map(winter => <tr key={winter.winter}>
              <th scope="row">{winter.winter}</th><td>{winter.reported} / {winter.expectedDays}</td><td>{winter.trace}</td>
              <td>{winter.absentDays + winter.missing + winter.flagged + winter.presumed_zero}</td><td>{winter.withObservationTime}</td>
            </tr>)}</tbody>
          </table></div>
          <a href={station.source.url} target="_blank" rel="noreferrer">NOAA station records ↗</a>
        </> : <p>{station.error}</p>}
      </details>)}
    </section>
  </>;
}
